import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Input,
  FormGroup,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";

import { handleLoginSuccess } from "../../Routes/authUtils";
import { hidePasswordWarning } from "../../slices/passwordWarningSlice";
import { handleChangePassword } from "../UserManagement/Constant/JsonConstant";
import CommonButton from "../Utils/CommonButton";
import { LabelText } from "../Utils/Input";

const PasswordWarningModal = () => {
  const dispatch = useDispatch();
  const signIn = useSelector((state) => state.SignIn.signin);
  const { show, response, navigate, setLoading } = useSelector(
    (state) => state.passwordWarning
  );

  const userId = localStorage.getItem("userId");

  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .min(8, "Password must be at least 8 characters long.")
      .matches(/[A-Z]/, "Must contain at least one uppercase letter.")
      .matches(/[a-z]/, "Must contain at least one lowercase letter.")
      .matches(/[0-9]/, "Must contain at least one number.")
      .matches(
        /[@$!%*?&#^()\-_=+{};:,<.>]/,
        "Must contain at least one special character."
      )
      .required("New Password is required"),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords do not match.")
      .required("Confirm Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        await handleChangePassword(
          values,
          dispatch,
          setLoading,
          navigate,
          userId
        );
        modalClose();
      } catch (err) {
        setErrors({
          confirmNewPassword: "Failed to change password. Try again.",
        });
        console.error("Password change error:", err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    handleLoginSuccess(response, dispatch, setLoading, navigate);
    dispatch(hidePasswordWarning());
  };

  const modalClose = () => {
    dispatch(hidePasswordWarning());
  };

  const lastChanged = new Date(signIn?.lastPasswordChanged);
  const daysSince = Math.floor(
    (Date.now() - lastChanged.getTime()) / (1000 * 60 * 60 * 24)
  );
  const remainingDays = 90 - daysSince;

  return (
    <Modal
      isOpen={show}
      toggle={handleClose}
      centered
      size="md"
      className="editBussinessProfileModal"
      backdrop={"false"}
    >
      <ModalHeader className="d-flex justify-content-center align-items-center rv-breadcrumb-border p-3">
        <div className="w-100 text-center fw-bold">
        {remainingDays > 0
            ? `Password about to Expire`
            : `Password Expired`}
        </div>
        {remainingDays > 0?
        <button
          type="button"
          className="close-button-currency mt-n1 fs-1 align-self-center"
          onClick={handleClose}
        >
          &times;
        </button> : <></>}
      </ModalHeader>

      <ModalBody>
        <p className="mb-4 w-100 text-center fw-bold fs-6">
          {remainingDays > 0
            ? `Your password is about to expire in ${remainingDays} days, please reset the password!`
            : `Your password has expired, please reset the password!`}
        </p>

        <form onSubmit={formik.handleSubmit}>
          {/* New Password */}
          <FormGroup>
          <LabelText htmlFor={"newPassword"} classLabelName={"fw-bold"} displayText={"New Password"} className={"text-danger"} important={"*"} />
            <LabelText
              htmlFor="newPassword"
              classLabelName={`d-block ms-0 rv-sub-label ${
                formik.touched.newPassword && formik.errors.newPassword
                  ? "rv-label-danger"
                  : "rv-label-muted"
              }`}
              displayText={
                formik.touched.newPassword && formik.errors.newPassword
                  ? formik.errors.newPassword
                  : "Enter New Password"
              }
            />
            <Input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.newPassword && !!formik.errors.newPassword}
            />
          </FormGroup>
          <FormGroup>
          <LabelText htmlFor={"confirmNewPassword"} classLabelName={"fw-bold"} displayText={"Confirm New Password"} className={"text-danger"} important={"*"} />
            <LabelText
              htmlFor="confirmNewPassword"
              classLabelName={`d-block ms-0 rv-sub-label ${
                formik.touched.confirmNewPassword &&
                formik.errors.confirmNewPassword
                  ? "rv-label-danger"
                  : "rv-label-muted"
              }`}
              displayText={
                formik.touched.confirmNewPassword &&
                formik.errors.confirmNewPassword
                  ? formik.errors.confirmNewPassword
                  : "Confirm New Password"
              }
            />
            <Input
              type="password"
              id="confirmNewPassword"
              name="confirmNewPassword"
              value={formik.values.confirmNewPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={
                formik.touched.confirmNewPassword &&
                !!formik.errors.confirmNewPassword
              }
            />
          </FormGroup>

          <ModalFooter className="d-flex justify-content-center border-0">
          {remainingDays > 0?
            <CommonButton
              outline
              className="btn-label rv-secondary-btn rounded-1 px-1 ps-3 py-1 w-md-auto text-nowrap"
              color="main-color"
              type="button"
              buttonAction={handleClose}
              buttonText="Close"
              icon={
                <i
                  className="ri-close-fill label-icon align-middle fs-16 me-1"
                  style={{
                    transform: "scaleX(-1)",
                    borderLeft: "1px solid",
                  }}
                ></i>
              }
              btnTxtClsName="px-2 ps-3 ms-2 me-1"
            />:<></>}
            <CommonButton
              color="secondary"
              type="submit"
              disabled={formik.isSubmitting}
              buttonText="Submit"
              icon={
                <i className="ri-checkbox-circle-line label-icon align-middle fs-16 me-1"></i>
              }
              className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap"
              btnTxtClsName="px-2 ps-3 ms-2 me-1"
            />
          </ModalFooter>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default PasswordWarningModal;
